import { createResource, type ResourceStoreContext } from '@atlassian/react-resource-router';
import { performGetRequest } from '@atlassian/jira-fetch';
import { fg } from '@atlassian/jira-feature-gating';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { getEnvironment } from '@atlassian/jira-common-util-get-tenant-context';
import { getStandardTrialTransactionIds } from '../utils/get-standard-trial-transaction-ids/index.tsx';
import type { TransactionAccountData, TransactionAccountStatuses } from './types.tsx';

export const TRANSACTION_ACCOUNT_STATUS_RESOURCE = 'TRANSACTION_ACCOUNT_STATUS_RESOURCE';
export const RESOURCE_KEY = 'transactionAccountStatusResource';
const FETCH_TIMEOUT_MS = 500; // 500ms test attempted fetch timeout

const fetchTransactionAccountStatus = (transactionAccountId: string) => {
	if (__SERVER__) {
		const hostEnv = getEnvironment() === 'prod' ? 'prod' : 'staging';
		return performGetRequest<TransactionAccountData>(
			`https://ccp-api-gateway.${hostEnv}.atl-paas.net/api/v1/status`,
			{
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-transaction-account': transactionAccountId,
					'x-ssr-service-name': 'ccp-api-gateway-v2',
				},
			},
		);
	}
	return performGetRequest<TransactionAccountData>('/gateway/api/ccp/api/v1/status', {
		headers: { 'Content-Type': 'application/json', 'X-transaction-account': transactionAccountId },
	});
};

export const getAllTransactionAccountStatuses = async (
	transactionAccountIds: string[],
): Promise<TransactionAccountData[]> => {
	try {
		let transactionAccountStatusTimeoutId: ReturnType<typeof setTimeout>;
		const timeoutPromise = new Promise<never>((_resolve, reject) => {
			transactionAccountStatusTimeoutId = setTimeout(
				() =>
					reject(
						new Error(
							`getAllTransactionAccountStatuses failed to complete within the ${FETCH_TIMEOUT_MS}ms timeout`,
						),
					),
				FETCH_TIMEOUT_MS,
			);
		});

		const transactionAccountStatusPromises = transactionAccountIds.map(
			fetchTransactionAccountStatus,
		);
		const transactionAccountStatusArray = await Promise.race([
			Promise.all(transactionAccountStatusPromises).finally(() => {
				if (transactionAccountStatusTimeoutId) {
					clearTimeout(transactionAccountStatusTimeoutId);
				}
			}),
			timeoutPromise,
		]);
		return transactionAccountStatusArray;
	} catch (err: unknown) {
		fireErrorAnalytics({
			meta: {
				id: 'getAllTransactionAccountStatuses',
				packageName: 'jiraRouterResourcesTransactionAccountStatus',
				teamName: 'growth-luna',
			},
			error: err instanceof Error ? err : undefined,
			sendToPrivacyUnsafeSplunk: true,
		});
		return [];
	}
};

export const standardTrialTransactionAccountStatusResource = resourceWithCondition2(
	() => fg('auto_upgrade_not_a_trial_jira_gate'),
	createResource<TransactionAccountStatuses>({
		type: TRANSACTION_ACCOUNT_STATUS_RESOURCE,
		getKey: () => RESOURCE_KEY,
		getData: async (_, { tenantContext }: ResourceStoreContext) => {
			// using util to get standard trial transaction account ids
			// it limits the util of the resource but separates logic
			const transactionAccountIds = getStandardTrialTransactionIds(tenantContext);
			const transactionAccountStatusArray =
				await getAllTransactionAccountStatuses(transactionAccountIds);
			return {
				transactionAccountDetails: transactionAccountStatusArray,
			};
		},
		isBrowserOnly: false,
	}),
);
